import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Loader2, Truck, DollarSign, Users, Ruler, Sun, 
  Award, TrendingUp, AlertCircle, ExternalLink, 
  CheckCircle, XCircle, MinusCircle, ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RV Match Assist - Internal Dealership Sales Tool
 * ================================================
 * Matches customers with the best RV options based on:
 * - Budget
 * - RV Type preference
 * - Sleep capacity needs
 * - Length requirements
 * - Off-grid preferences
 * - Tow vehicle capabilities
 */
const RVMatchAssist = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  // Quiz form state
  const [formData, setFormData] = useState({
    location: 'wbma',
    budget_max: '',
    rv_type: '',
    sleep_capacity: '',
    length_max: '',
    off_grid: false,
    max_tow: '',
    max_hitch: ''
  });

  // Handle form input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Submit match request
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.budget_max || !formData.rv_type || !formData.sleep_capacity) {
      toast.error('Please fill in required fields: Budget, RV Type, and Sleep Capacity');
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const payload = {
        location: formData.location,
        quiz: {
          budget_max: parseInt(formData.budget_max),
          rv_type: formData.rv_type,
          sleep_capacity: parseInt(formData.sleep_capacity),
          length_max: formData.length_max ? parseInt(formData.length_max) : 0,
          off_grid: formData.off_grid
        },
        tow_vehicle: (formData.max_tow || formData.max_hitch) ? {
          max_tow: formData.max_tow ? parseInt(formData.max_tow) : 0,
          max_hitch: formData.max_hitch ? parseInt(formData.max_hitch) : 0
        } : null
      };

      const response = await axios.post(`${API}/assist/match`, payload);
      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.warning('No matching RVs found. Try adjusting your criteria.');
      } else {
        toast.success(`Found ${response.data.length} matching RV${response.data.length > 1 ? 's' : ''}!`);
      }
    } catch (error) {
      console.error('Match error:', error);
      toast.error('Failed to find matches. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      location: 'wbma',
      budget_max: '',
      rv_type: '',
      sleep_capacity: '',
      length_max: '',
      off_grid: false,
      max_tow: '',
      max_hitch: ''
    });
    setResults(null);
  };

  // Get label badge styles
  const getLabelStyles = (label) => {
    if (label.includes('Best Match')) {
      return 'bg-green-100 text-green-800 border-green-300';
    } else if (label.includes('Best Value')) {
      return 'bg-blue-100 text-blue-800 border-blue-300';
    } else {
      return 'bg-amber-100 text-amber-800 border-amber-300';
    }
  };

  // Get label icon
  const getLabelIcon = (label) => {
    if (label.includes('Best Match')) {
      return <Award className="w-4 h-4" />;
    } else if (label.includes('Best Value')) {
      return <TrendingUp className="w-4 h-4" />;
    } else {
      return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Get reason icon
  const getReasonIcon = (reason) => {
    if (reason.startsWith('✓')) {
      return <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />;
    } else if (reason.startsWith('✗')) {
      return <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
    } else {
      return <MinusCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="rv-match-assist">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#004E86] to-[#00619D] text-white py-4 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dealer')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft size={18} className="mr-1" />
              Dashboard
            </Button>
            <div className="h-6 w-px bg-white/30" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                RV MATCH ASSIST
              </h1>
              <p className="text-xs sm:text-sm text-white/70">Internal Sales Tool</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw size={16} className="mr-1" />
            Reset
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Quiz Form */}
          <div className="lg:col-span-1">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 sm:p-6 space-y-4">
              <h2 className="text-lg font-bold text-[#004E86] border-b pb-2">Customer Form</h2>
              
              {/* Location */}
              <div>
                <Label htmlFor="location">Dealership Location</Label>
                <Select value={formData.location} onValueChange={(v) => handleChange('location', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wbma">Camping World WBMA</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Budget */}
              <div>
                <Label htmlFor="budget" className="flex items-center gap-2">
                  <DollarSign size={16} className="text-green-600" />
                  Maximum Budget *
                </Label>
                <Input
                  id="budget"
                  type="number"
                  placeholder="e.g., 50000"
                  value={formData.budget_max}
                  onChange={(e) => handleChange('budget_max', e.target.value)}
                  required
                />
              </div>

              {/* RV Type */}
              <div>
                <Label htmlFor="rv_type" className="flex items-center gap-2">
                  <Truck size={16} className="text-[#004E86]" />
                  RV Type *
                </Label>
                <Select value={formData.rv_type} onValueChange={(v) => handleChange('rv_type', v)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select RV type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Type</SelectItem>
                    <SelectItem value="travel_trailer">Travel Trailer</SelectItem>
                    <SelectItem value="fifth_wheel">Fifth Wheel</SelectItem>
                    <SelectItem value="motorhome">Any Motorhome</SelectItem>
                    <SelectItem value="motorhome_class_a">Class A Motorhome</SelectItem>
                    <SelectItem value="motorhome_class_b">Class B Motorhome</SelectItem>
                    <SelectItem value="motorhome_class_c">Class C Motorhome</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sleep Capacity */}
              <div>
                <Label htmlFor="sleeps" className="flex items-center gap-2">
                  <Users size={16} className="text-purple-600" />
                  Sleep Capacity *
                </Label>
                <Input
                  id="sleeps"
                  type="number"
                  placeholder="e.g., 4"
                  min="1"
                  max="12"
                  value={formData.sleep_capacity}
                  onChange={(e) => handleChange('sleep_capacity', e.target.value)}
                  required
                />
              </div>

              {/* Max Length */}
              <div>
                <Label htmlFor="length" className="flex items-center gap-2">
                  <Ruler size={16} className="text-orange-600" />
                  Max Length (ft)
                </Label>
                <Input
                  id="length"
                  type="number"
                  placeholder="e.g., 35 (optional)"
                  value={formData.length_max}
                  onChange={(e) => handleChange('length_max', e.target.value)}
                />
              </div>

              {/* Off-Grid */}
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="offgrid"
                  checked={formData.off_grid}
                  onChange={(e) => handleChange('off_grid', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <Label htmlFor="offgrid" className="flex items-center gap-2 cursor-pointer">
                  <Sun size={16} className="text-yellow-600" />
                  Off-Grid Package Preferred
                </Label>
              </div>

              {/* Tow Vehicle Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Truck size={16} />
                  Tow Vehicle (Optional)
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="max_tow" className="text-xs">Max Tow (lbs)</Label>
                    <Input
                      id="max_tow"
                      type="number"
                      placeholder="e.g., 10000"
                      value={formData.max_tow}
                      onChange={(e) => handleChange('max_tow', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_hitch" className="text-xs">Max Hitch (lbs)</Label>
                    <Input
                      id="max_hitch"
                      type="number"
                      placeholder="e.g., 1000"
                      value={formData.max_hitch}
                      onChange={(e) => handleChange('max_hitch', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#F2A900] text-black hover:bg-[#D99700] font-bold py-3"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Finding Matches...
                  </>
                ) : (
                  'Find Best Matches'
                )}
              </Button>
            </form>
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            {!results && !isLoading && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Ready to Match</h3>
                <p className="text-gray-500 text-sm">
                  Fill out the customer quiz to find the top 3 matching RVs from inventory.
                </p>
              </div>
            )}

            {isLoading && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <Loader2 className="w-12 h-12 text-[#004E86] mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-semibold text-gray-600">Analyzing Inventory...</h3>
                <p className="text-gray-500 text-sm">Finding the best matches for your customer</p>
              </div>
            )}

            {results && results.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Matches Found</h3>
                <p className="text-gray-500 text-sm">
                  Try adjusting the budget, RV type, or other criteria to find matches.
                </p>
              </div>
            )}

            {results && results.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-[#004E86]">
                  Top {results.length} Match{results.length > 1 ? 'es' : ''}
                </h2>
                
                {results.map((match, index) => (
                  <div
                    key={match.rv.stock}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 border-b flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-[#004E86]">#{index + 1}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">
                            {match.rv.year} {match.rv.brand} {match.rv.model}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Stock: {match.rv.stock} | VIN: {match.rv.vin}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold border ${getLabelStyles(match.label)}`}>
                          {getLabelIcon(match.label)}
                          {match.label}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">Score: {match.score}/100</p>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-gray-500">Price</p>
                          <p className="text-lg font-bold text-green-700">${match.rv.price.toLocaleString()}</p>
                          {match.rv.msrp > match.rv.price && (
                            <p className="text-xs text-gray-400 line-through">${match.rv.msrp.toLocaleString()}</p>
                          )}
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-gray-500">Sleeps</p>
                          <p className="text-lg font-bold text-purple-700">{match.rv.sleeps}</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-lg">
                          <p className="text-xs text-gray-500">Length</p>
                          <p className="text-lg font-bold text-orange-700">{match.rv.length}'</p>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-500">Type</p>
                          <p className="text-sm font-bold text-blue-700 capitalize">
                            {match.rv.type.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>

                      {/* Reasons */}
                      <div className="border-t pt-3">
                        <p className="text-xs font-semibold text-gray-500 mb-2">Match Reasons:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {match.reasons.map((reason, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              {getReasonIcon(reason)}
                              <span className="text-gray-700">{reason.substring(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="border-t pt-3 mt-3 flex justify-end">
                        <Button
                          onClick={() => window.open(match.vinsolutions_link, '_blank')}
                          className="bg-[#004E86] text-white hover:bg-[#003d6b]"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Open in VinSolutions
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RVMatchAssist;
